import React from "react";
import SEO from "../components/seo";
import TitleBannerEnquiry from "../components/titleBanner/TitleBannerEnquiry";
import { getPseaPageJsonLd } from "../utils/jsonld";

const ACCREDIFY_SRC = "https://component.nexus.accredify.io/index.umd.js";

export default function Verify() {
    const [ready, setReady] = React.useState(false);

    React.useEffect(() => {
        const existing = document.querySelector(`script[src="${ACCREDIFY_SRC}"]`);

        if (existing && existing.dataset.loaded === "1") {
            setReady(true);
            return;
        }

        const s = existing || document.createElement("script");
        if (!existing) {
            s.src = ACCREDIFY_SRC;
            s.async = true;
            document.body.appendChild(s);
        }

        const onLoad = () => {
            s.dataset.loaded = "1";
            setReady(true);
        };

        s.addEventListener("load", onLoad);
        s.addEventListener("error", () => {
            console.error("Failed to load Accredify script.");
        });

        return () => {
            s.removeEventListener("load", onLoad);
        };
    }, []);

    return (
        <>
            <SEO
                title="BIM & CAD Training Center | Ace Industrial Academy"
                description="Ace Industrial Academy is a leading training center in Singapore for IT courses, BIM courses, 3D CAD courses, 4D courses, software courses, building information modeling courses and more."
                jsonLd={getPseaPageJsonLd()}
            />

            <TitleBannerEnquiry
                title="Verify Certificate"
                description=""
                withOnlyDescription
                hasPlainBanner
            />

            {/* 📝 Instructions section (justified) */}
            <section
                style={{
                    maxWidth: "1340px",
                    margin: "40px auto 24px",
                    padding: "0 20px",
                    textAlign: "justify",
                    lineHeight: 1.7,
                    fontSize: 16,
                }}
            >
                <p>
                    This page can help you verify the authenticity of the digital certificates
                    issued to participants of our programmes. The digital certificates are in the
                    OpenCert format (<code>.opencert</code>). OpenCert leverages on blockchain
                    technologies, and is a trademark for publishing educational credentials,
                    developed by GovTech Singapore.
                </p>
                <p style={{ marginTop: 16 }}>
                    You can learn more about OpenCerts at{" "}
                    <a href="https://opencerts.io" target="_blank" rel="noopener noreferrer">
                        https://opencerts.io
                    </a>.
                </p>
            </section>

            {/* 📎 Styled container around the Accredify component */}
            <div
                style={{
                    maxWidth: 1145,
                    margin: "0 auto 40px",
                    padding: "28px 20px",
                    background: "#fafafa",
                    border: "1px dashed #cfd8dc",
                    borderRadius: 12,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                    textAlign: "center",
                }}
            >
                <div id="cert-app" style={{ minHeight: 400 }}>
                    {!ready ? (
                        <p style={{ margin: 0 }}>Loading verification component…</p>
                    ) : (
                        <render-cert
                            access-token="eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJhdWQiOiJhMDNhN2UwYS01MjZkLTQ3MWQtODNmNC1kYzIzYTlhNjUyYTUiLCJqdGkiOiJjZjI0ODdmNTNkN2I1NjIxMGFkODgyMzEyNDJmMjk5ZmJlNWRjMWUxZjI4ZjMyNDdkMmIzMjc4NGU4MzMyODQ3ZjNlNmQwZWE4MzQwMWFkNCIsImlhdCI6MTc2MjQyMDE3Mi4xNTY2MDQsIm5iZiI6MTc2MjQyMDE3Mi4xNTY2MDcsImV4cCI6MTc5Mzk1NjE3Mi4xNTAzNzQsInN1YiI6IiIsInNjb3BlcyI6WyJ3ZWJjb21wb25lbnQtdmVyaWZpY2F0aW9uIl19.T5Sgf3A3W-bJvW8tuRJT3t27PrOcJr6haz2HTWQjkAg9YuV-GgLuhA8Su_QFIIh8l4RgtOicXWMiDVnnE5Mh71sotXAqvQCdzZ5_Pux-css-yyb_gzzxTs3Ae45qc1QDuo3S6EA0Ho5woYfI2Dnlc3A5S-ia_uJwbr_Z-57OF4-70g7J9RM6_xno8GIrfS3qpyBRnhhpAkmpnSnjQMHsqYeo6xPcMnGKtrMt1GmUkNYpQ43ufRoK8hr6K5JMr_aF_y6ECYSapi9aw8jbLxwO18GSAG4HcXLODH1M3m-3O9NLZXtVv0YB8SRQPfGNgFPN1p-OhPZdyUYroJrbldGANnh5d4bNxDIKCVrMvwS2KFSXXehj1ULya6LOovj7cc0r_6DO6-KC2IXk1OL_ZgENv8PkfeOqiRgpcL_bQ-7XkjlgasvEWfUwAvyXDjxc2LlTssGm7VnAvkMAqKblw_a87vZgatV1xKSA_U6weSW9gE28WPJ29VGpLNvKq_AQFDya8MkpNyI9IjAb9djJE5kSSHmOOWYjqBa2K88BQuFnP-Yoq0HotL882XAQOr05sDtxw9ebHv4W5J7Q2rJwOIGeWm1F6SJSmFb2XZCOkIrv-A8q1G3_xgr9H9kZhD252P4Y9D1wR99bvMgHzhTDuPfuTBs5B7tPZuks0GrCAu0bQnE"
                        />
                    )}
                </div>

                {/* Mimic tiny footer area below upload (optional; remove if Accredify already renders its own) */}
                <div style={{ marginTop: 20, fontSize: 13, color: "#607d8b" }}>
                    <span style={{ marginRight: 10 }}>
                        Accredify Pte. Ltd. © {new Date().getFullYear()}
                    </span>

                    <a
                        href="https://accredify.io/LegalPolicies/master-user-agreement"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ marginRight: 10 }}
                    >
                        Terms of Service
                    </a>
                    <a
                        href="https://accredify.io/LegalPolicies/data-protection-notice"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Privacy Policy
                    </a>
                </div>
            </div>

            <section
                style={{
                    maxWidth: "1340px",
                    margin: "40px auto 24px",
                    padding: "0 20px",
                    textAlign: "justify",
                    lineHeight: 1.7,
                    fontSize: 16,
                }}
            >
                <p style={{ marginTop: 16 }}>
                    <strong>Instructions:</strong> Upload or drag and drop the OpenCert file into
                    the certificate verifier below. The section below will automatically show the
                    results of the verification, and also render the certificate if it is authentic.
                    If you face any issue, feel free to contact our partner, Accredify, at{" "}
                    <a href="https://accredify.io" target="_blank" rel="noopener noreferrer">
                        https://accredify.io
                    </a>.
                </p>
            </section>
        </>
    );
}
